<!--
  ReplaceScopeDialog -- the scope question after a placement card's
  "Replace" pick (Jake, 2026-10-06: "Replace = rebind, then ask the scope").

    - "This product only"           -> 'this-product'
    - "Every product using <old>"   -> 'every-product'. Says how many
      products, and in which stores, BEFORE the user confirms, so it stays
      disabled until that summary (GET /api/artworks/<old>/rebind_scope,
      fetched by the host) has loaded.
    - Cancel / scrim / Esc          -> 'cancel'

  Neither option edits a library decoration: both re-point placements to a
  decoration of the picked artwork, and the old decoration stays in the
  library for anyone still using it.

  CONFIRM FIRST, BECAUSE IT CAN COST MONEY (Jake, 2026-10-06). A replace
  creates a new decoration unless a compatible one already exists, and that
  can cost: digitizing for embroidery, art prep for other methods, and an
  embroidery price that follows the new design's stitch count. When any of
  that applies the dialog says which, per option, and both options stay
  disabled until the user ticks the confirmation.

  LIVE STORES NEED APPROVAL FIRST (Jake, 2026-10-07, option A). On a live
  store a replace applies only once the store's client has approved the
  new art; until then that store keeps its current decoration. The dialog
  splits "applies now" from "needs approval first". Approval is PER SIZE:
  for a held store the replace PREPARES the new decoration at each size
  without binding it, and the host then opens the existing send-for-approval
  for it. There is deliberately no "send" button here: opened before the
  decoration existed, the send was refused. Approval itself is free.

  Presentational only: the host owns the fetch and the writes. Exactly one
  outcome is emitted per interaction -- the same closingFromAction guard as
  DestructiveConfirmDialog, because Vuetify echoes our own close back as
  update:model-value=false and that echo must not read as a cancel.
-->
<template>
    <v-dialog
        :model-value="modelValue"
        max-width="520"
        @update:model-value="onUpdate"
    >
        <v-card data-testid="replace-scope-dialog">
            <v-card-title>Replace artwork</v-card-title>
            <v-card-text>
                <p class="rsd-lede">
                    Replace &ldquo;{{ oldArtworkName }}&rdquo; with
                    &ldquo;{{ newArtworkName }}&rdquo; on:
                </p>
                <p
                    v-if="loading"
                    data-testid="replace-scope-loading"
                    class="rsd-muted"
                >
                    Checking where &ldquo;{{ oldArtworkName }}&rdquo; is used&hellip;
                </p>
                <p
                    v-else-if="error || !scope"
                    data-testid="replace-scope-error"
                    class="rsd-muted"
                >
                    Couldn't load where &ldquo;{{ oldArtworkName }}&rdquo; is
                    used, so only this product can be changed right now.
                </p>
                <div
                    v-else
                    data-testid="replace-scope-summary-loaded"
                >
                    <p data-testid="replace-scope-summary">
                        &ldquo;{{ oldArtworkName }}&rdquo; is on
                        {{ plural(scope.product_count, "product") }} in
                        {{ plural(scope.store_count, "store") }}:
                    </p>
                    <ul class="rsd-stores">
                        <li
                            v-for="store in scope.stores"
                            :key="store.id"
                            :data-testid="`replace-scope-store-${store.id}`"
                        >
                            {{ store.name || "Untitled store" }}
                            &middot; {{ plural(store.product_count, "product") }}
                        </li>
                    </ul>
                </div>
                <p
                    v-if="liveStoreCount > 0"
                    data-testid="replace-scope-live"
                >
                    {{ plural(liveStoreCount, "live store") }}
                    {{ liveStoreCount === 1 ? "uses" : "use" }}
                    &ldquo;{{ oldArtworkName }}&rdquo;.
                </p>
                <div
                    v-if="hasHeld"
                    data-testid="replace-scope-approval"
                    class="rsd-approval"
                >
                    <p
                        v-if="appliesNow"
                        data-testid="replace-scope-applies-now"
                    >
                        Applies now:
                        {{ plural(appliesNow.product_count, "product") }} in
                        {{ plural(appliesNow.store_count, "store") }}.
                    </p>
                    <p
                        v-if="needsApproval && needsApproval.product_count"
                        data-testid="replace-scope-needs-approval"
                    >
                        Needs approval first:
                        {{ plural(needsApproval.product_count, "product") }} in
                        {{ plural(needsApproval.store_count, "live store") }}
                        ({{ heldStoreNames }}). The current decoration keeps
                        selling there: replacing prepares
                        &ldquo;{{ newArtworkName }}&rdquo; at each size and
                        then lets you send it to the client for approval.
                    </p>
                    <p
                        v-if="placementNeedsApproval"
                        data-testid="replace-scope-this-needs-approval"
                    >
                        This store is live: &ldquo;This product only&rdquo;
                        prepares &ldquo;{{ newArtworkName }}&rdquo; at this
                        size and lets you send it for the client's approval.
                        The current decoration keeps selling until it is
                        approved; then replace again.
                    </p>
                    <p class="rsd-muted">
                        Approval is free.
                    </p>
                </div>
                <div
                    v-if="costApplies"
                    data-testid="replace-scope-cost"
                    class="rsd-cost"
                >
                    <p>
                        Replacing creates a new decoration, which may cost
                        money<template v-if="costUnknown">.</template><template v-else>:</template>
                    </p>
                    <ul v-if="!costUnknown">
                        <li v-if="mayNeedDigitizing">
                            Embroidery: the new design may need digitizing
                            when it is sent for approval, a paid art-prep
                            step.
                        </li>
                        <li v-if="artPrepMethods.length">
                            {{ artPrepMethods.join(", ") }}: the new design
                            may need art prep when it is sent for approval,
                            which can carry a fee.
                        </li>
                        <li v-if="embroideryPriceMayChange">
                            Embroidery is priced by stitch count, so the
                            per-item price can change.
                        </li>
                    </ul>
                    <p
                        v-if="!costUnknown && !everyProductOnly"
                        data-testid="replace-scope-this-product-cost"
                    >
                        This product only: {{ newDecorationsText(placementCost) }}.
                    </p>
                    <p
                        v-if="!costUnknown"
                        data-testid="replace-scope-every-product-cost"
                    >
                        Every product: {{ newDecorationsText(everyCost) }}.
                    </p>
                    <!--
                      A native checkbox, not v-checkbox: Vuetify routes data-*
                      attributes to v-checkbox's ROOT div, so the testid
                      would name a wrapper a click can miss, not the control.
                    -->
                    <label class="rsd-confirm">
                        <input
                            v-model="costConfirmed"
                            type="checkbox"
                            data-testid="replace-scope-confirm-cost"
                        >
                        I understand this may cost money.
                    </label>
                </div>
                <p class="rsd-muted">
                    Your decoration library is not changed.
                </p>
            </v-card-text>
            <v-card-actions class="rsd-actions">
                <v-btn
                    variant="text"
                    data-testid="replace-scope-cancel"
                    @click="choose('cancel')"
                >
                    Cancel
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="!everyProductOnly"
                    variant="outlined"
                    :disabled="!costCleared"
                    data-testid="replace-scope-this-product"
                    @click="choose('this-product')"
                >
                    This product only
                </v-btn>
                <v-btn
                    color="primary"
                    variant="flat"
                    :disabled="!canChooseEveryProduct"
                    data-testid="replace-scope-every-product"
                    @click="choose('every-product')"
                >
                    Every product using &ldquo;{{ oldArtworkName }}&rdquo;
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { computed, ref } from "vue";

const props = defineProps({
    modelValue: { type: Boolean, required: true },
    oldArtworkName: { type: String, required: true },
    newArtworkName: { type: String, required: true },
    // GET rebind_scope payload: { product_count, store_count, stores: [...] }
    scope: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
    // The Decoration Library artwork page has no product in hand, so "This
    // product only" has no meaning there (Bug Intake #297): hide it.
    everyProductOnly: { type: Boolean, default: false },
});

const emit = defineEmits([
    "update:modelValue",
    "this-product",
    "every-product",
    "cancel",
]);

const everyCost = computed(() => props.scope?.cost ?? null);
const placementCost = computed(() => props.scope?.placement_cost ?? null);
const costs = computed(() => [everyCost.value, placementCost.value].filter(Boolean));
const liveStoreCount = computed(() => Number(props.scope?.live_store_count) || 0);
const mayNeedDigitizing = computed(() => costs.value.some((c) => c.may_need_digitizing));
const embroideryPriceMayChange = computed(
    () => costs.value.some((c) => c.embroidery_price_may_change),
);
const ART_PREP_LABELS = { dtf: "DTF", screen_print: "Screen print" };
const artPrepMethods = computed(() => [
    ...new Set(
        costs.value.flatMap((c) => c.art_prep_methods ?? []).map(
            (slug) => ART_PREP_LABELS[slug] ?? slug,
        ),
    ),
]);
// The preview failed: what a replace costs is unknown, so warn generically
// and still require the confirmation (never let "unknown" read as "free").
const costUnknown = computed(() => !props.loading && (props.error != null || props.scope == null));
// Anything that can cost money: a decoration to create, or a price that
// follows the new stitch count -- or a cost we could not determine.
const costApplies = computed(
    () => costUnknown.value
        || costs.value.some((c) => Number(c.new_decoration_count) > 0)
        || embroideryPriceMayChange.value,
);
const costConfirmed = ref(false);
// Neither option until the cost is known (or the failure acknowledged).
const costCleared = computed(
    () => !props.loading && (!costApplies.value || costConfirmed.value),
);

function newDecorationsText(cost) {
    // One card of a split light/dark pair (card #307): the placement keeps
    // its decoration and only that card's artwork changes.
    if (cost?.keeps_decoration) return "changes only this card's artwork";
    const n = Number(cost?.new_decoration_count) || 0;
    if (n === 0) return "reuses an existing decoration";
    return `creates ${plural(n, "new decoration")}`;
}

// Option A split: present only when the host passed the new artwork, so
// the server could check approvals.
const appliesNow = computed(() => props.scope?.applies_now ?? null);
const needsApproval = computed(() => props.scope?.needs_approval ?? null);
const placementNeedsApproval = computed(() => props.scope?.placement_needs_approval === true);
const heldStoreIds = computed(() => [
    ...new Set([
        ...(props.scope?.placement_held?.store_ids ?? []),
        ...(needsApproval.value?.store_ids ?? []),
    ]),
].sort((a, b) => a - b));
const hasHeld = computed(() => heldStoreIds.value.length > 0);
const heldStoreNames = computed(
    () => (needsApproval.value?.stores ?? [])
        .map((s) => s.name || "Untitled store")
        .join(", "),
);

const canChooseEveryProduct = computed(
    () => !props.loading && !props.error && props.scope != null
        && costCleared.value
        && (
            appliesNow.value == null
            || Number(appliesNow.value.product_count) > 0
            || Number(needsApproval.value?.product_count) > 0
        ),
);

function plural(n, noun) {
    const count = Number(n) || 0;
    return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

// See the header: our own close echoes back through onUpdate.
let closingFromAction = false;

function onUpdate(v) {
    emit("update:modelValue", v);
    if (v) return;
    if (closingFromAction) {
        closingFromAction = false;
        return;
    }
    emit("cancel");
}

function choose(outcome) {
    closingFromAction = true;
    emit("update:modelValue", false);
    emit(outcome);
}

</script>

<style scoped>
.rsd-lede {
    margin-bottom: 8px;
}
.rsd-muted {
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.875rem;
    margin-top: 8px;
}
.rsd-stores {
    margin: 4px 0 0 20px;
}
.rsd-approval {
    margin-top: 12px;
    padding: 8px 12px;
    border-radius: 6px;
    background: rgba(25, 118, 210, 0.08);
}
.rsd-cost {
    margin-top: 12px;
    padding: 8px 12px;
    border-radius: 6px;
    background: rgba(255, 193, 7, 0.12);
}
.rsd-cost ul {
    margin: 4px 0 8px 20px;
}
.rsd-confirm {
    display: flex;
    gap: 8px;
    align-items: center;
    font-weight: 500;
    cursor: pointer;
}
.rsd-actions {
    flex-wrap: wrap;
    gap: 8px;
}
</style>
